import React, { useEffect, useState } from "react";

import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Input from "@material-ui/core/Input";
import InputAdornment from "@material-ui/core/InputAdornment";
import LinearProgress from "@material-ui/core/LinearProgress";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import logo from "../../assets/images/logo-neutro-large.png";
import { ROUTES } from "../../routes/routes";
import { saveAccessLogs } from "../../services/peopleManagement.service";
import { AppState } from "../../store";
import { authGDPUser, login } from "../../store/actions/authActions";
import { AuthReducerState } from "../../store/reducers/authReducer";

import ForgotPasswordDialog from "./ForgotPasswordDialog";

const useStyles = makeStyles(() =>
  createStyles({
    linearProgress: {
      position: "absolute",
      top: 0,
      width: "100%",
      height: "5px",
    },
    authContainer: {
      margin: "0 auto",
      padding: "0 35px",
    },
    logoLello: {
      background: `url(${logo}) no-repeat`,
      backgroundPosition: "center",
      backgroundSize: "contain",
      height: "128px",
      marginTop: "100px",
      marginBottom: "70px",

      "@media(max-width: 600px)": {
        marginBottom: "20px",
        marginTop: "50px",
      },
    },
    title: {
      letterSpacing: "0.15em",
      fontWeight: "bolder",
      fontSize: "30px",
    },
    descriptionText: {
      fontSize: "15px",
      letterSpacing: "0.03em",
      fontWeight: 500,
      textAlign: "center",
    },
    textField: {
      marginTop: "20px",
    },
    buttonContainer: {
      marginTop: "40px",
    },
    authenticationMessage: {
      textAlign: "center",
      marginTop: "20px",
      fontWeight: "bold",
      height: "20px",
    },
  }),
);

const gdpParams = {
  ACCESS_TOKEN: "accessToken",
};

const Authentication = () => {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingAccessToken, setLoadingAccessToken] = useState(false);

  const {
    loading,
    errorMessage,
    sessionExpired,
  }: AuthReducerState = useSelector((state: AppState) => state.authReducer);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const accessToken = query.get(gdpParams.ACCESS_TOKEN);

    const fillInitialData = async () => {
      if (accessToken) {
        setLoadingAccessToken(true);
        await dispatch(authGDPUser(accessToken));
        setLoadingAccessToken(false);
        history.push(ROUTES.PEOPLE_MANAGEMENT.path);
        await saveAccessLogs(accessToken);
      }
    };
    fillInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (user && password) {
      dispatch(
        login(user, password, () => {
          history.push(ROUTES.PEOPLE_MANAGEMENT.path);
        }),
      );
    }
  };

  const handleForgotPasswordDialogClose = () => {
    setOpenForgotPassword(false);
  };
  return (
    <>
      {loading && (
        <LinearProgress className={classes.linearProgress} color="primary" />
      )}
      <Grid
        container
        alignContent="center"
        className={classes.authContainer}
        direction="column"
      >
        <div className={classes.logoLello} />
        <Grid item xs={12}>
          <Typography
            className={classes.title}
            align="center"
            color="primary"
            variant="h4"
          >
            Bem Vindo
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography
            className={classes.descriptionText}
            color="textSecondary"
            variant="body2"
          >
            Acesse sua conta com usuário ou CNPJ do seu condomínio.
          </Typography>
        </Grid>

        <Grid item xs={12} className={classes.textField}>
          <TextField
            fullWidth
            disabled={loadingAccessToken}
            autoComplete="off"
            margin="dense"
            color="primary"
            id="user"
            placeholder="Usuário"
            value={user}
            onChange={(event: any) => setUser(event.target.value)}
          />
        </Grid>

        <Grid item xs={12} className={classes.textField}>
          <Input
            margin="dense"
            fullWidth
            disabled={loadingAccessToken}
            id="password"
            autoComplete="off"
            placeholder="Senha"
            color="primary"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(event.target.value)}
            endAdornment={
              <InputAdornment position="end">
                {/* <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    setOpenForgotPassword(true);
                  }}
                >
                  Esqueci a senha
                </Link> */}
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />
        </Grid>

        <Grid item xs={12} className={classes.buttonContainer}>
          <Grid container>
            <Grid item xs={8}>
              <FormControlLabel
                control={<Checkbox value="checkedB" color="primary" />}
                color="secondary"
                label="Lembrar-me"
              />
            </Grid>
            <Grid item xs={4}>
              <Button
                fullWidth
                disabled={loading || loadingAccessToken}
                onClick={handleSubmit}
                size="large"
                variant="contained"
                color="primary"
                type="submit"
              >
                Entrar
              </Button>
            </Grid>

            {sessionExpired && (
              <Grid item xs={12}>
                <Typography
                  color="primary"
                  variant="body1"
                  className={classes.authenticationMessage}
                >
                  Sessão expirada. Efetue login novamente.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography
                color="primary"
                variant="body1"
                className={classes.authenticationMessage}
              >
                {errorMessage}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <ForgotPasswordDialog
          open={openForgotPassword}
          handleClose={handleForgotPasswordDialogClose}
        />
      </Grid>
    </>
  );
};

export default Authentication;
